import { z } from 'zod';
import { encodeFieldName } from './fieldNameCodec';
import { FIELD_TYPE_MAP } from './validation';

/**
 * Get field type from discriminated union FormItem
 * @param {FormItem} field - Form item (discriminated union)
 * @returns {string} Field type
 */
export function getFieldType(field) {
  if (field.text && 'type' in field.text) {
    const textField = field.text;
    if (textField.type === 'int') return 'int';
    if (textField.type === 'email') return 'email';
    return 'text';
  }
  if (field.textarea && 'type' in field.textarea) {
    const textareaField = field.textarea;
    if (textareaField.type === 'wysiwyg') return 'wysiwyg';
    return 'textarea';
  }
  if (field.select) return 'enum';
  if (field.selectMultiple) return 'enum';
  if (field.selectClass) return 'enum';
  if (field.selectMultipleClass) return 'enum';
  if (field.file) return 'file';
  if (field.color) return 'color';
  if (field.date) return 'datetime';
  if (field.time) return 'time';
  if (field.boolean) return 'boolean';
  if (field.mergeTable) return 'mergeTable';
  if (field.entity) return 'entity';
  if (field.checkbox) return 'checkbox';
  if (field.checkboxList) return 'checkboxList';
  if (field.radioList) return 'radioList';
  if (field.radio) return 'radio';
  if (field.grid) return 'grid';
  return 'text';
}

/**
 * Get field data from discriminated union FormItem
 * @param {FormItem} field - Form item (discriminated union)
 * @returns {FormItemData | null} Field data object
 */
export function getFieldData(field) {
  if (field.text) return field.text;
  if (field.textarea) return field.textarea;
  if (field.select) return field.select;
  if (field.selectMultiple) return field.selectMultiple;
  if (field.selectClass) return field.selectClass;
  if (field.selectMultipleClass) return field.selectMultipleClass;
  if (field.file) return field.file;
  if (field.color) return field.color;
  if (field.date) return field.date;
  if (field.time) return field.time;
  if (field.boolean) return field.boolean;
  if (field.mergeTable) return field.mergeTable;
  if (field.entity) return field.entity;
  if (field.checkbox) return field.checkbox;
  if (field.checkboxList) return field.checkboxList;
  if (field.radioList) return field.radioList;
  if (field.radio) return field.radio;
  if (field.grid) return field.grid;
  return null;
}

/**
 * Get upload URL for file fields (only FormItemFile has uploadUrl)
 * @param {FormItem} field - Form item
 * @returns {string | undefined} Upload URL when field is a file field, undefined otherwise
 */
export function getFileUploadUrl(field) {
  const fileField = /** @type {{ uploadUrl?: string } | null} */ (field.file);
  return fileField?.uploadUrl;
}

/**
 * Get field ID from FormItem
 * @param {FormItem} field - Form item
 * @returns {string | null} Field ID
 */
export function getFieldId(field) {
  const fieldData = getFieldData(field);
  return fieldData?.id || null;
}

/**
 * Iterate grid rows including subRows (single nesting level).
 * @param {FormTableGroup[]} groups
 * @param {(row: FormTableRow) => void} callback
 */
export function iterateGridRows(groups, callback) {
  (groups ?? []).forEach((group) => {
    (group.rows ?? []).forEach((row) => {
      callback(row);
      (row.subRows ?? []).forEach((subRow) => callback(subRow));
    });
  });
}

/**
 * @param {FormItem} field
 * @param {(ctx: { formItem: FormItem, row: FormTableRow }) => void} callback
 */
function forEachGridInnerFieldInRows(field, callback) {
  if (!field.grid?.table?.groups) return;
  iterateGridRows(field.grid.table.groups, (row) => {
    (row.cells ?? []).forEach((cell) => {
      const formItem = cell.field?.formItem;
      if (!formItem) return;
      if (getFieldType(formItem) === 'grid') return;
      callback({ formItem, row });
    });
  });
}

/**
 * Extract nested form items from a grid field's table cells.
 * Nested grids are filtered out (no grid-in-grid).
 * @param {FormItem} field - Form item (expected to be a grid)
 * @returns {FormItem[]}
 */
export function getGridInnerFields(field) {
  /** @type {FormItem[]} */
  const innerFields = [];
  forEachGridInnerFieldInRows(field, ({ formItem }) => {
    innerFields.push(formItem);
  });
  return innerFields;
}

/**
 * Flatten fieldset fields for value lifecycle (defaults, zod, sanitize, submit).
 * Grid fields are replaced by their inner form items (grid itself has no value).
 * @param {FormItem[]} fields
 * @returns {FormItem[]}
 */
export function getSubmittableFields(fields) {
  if (!Array.isArray(fields)) return [];
  /** @type {FormItem[]} */
  const result = [];
  /** @type {Map<string, number>} */
  const radioIndexById = new Map();

  /**
   * @param {FormItem} field
   */
  const pushField = (field) => {
    if (getFieldType(field) === 'radio') {
      const radioId = getFieldId(field);
      if (!radioId) return;
      const existingIndex = radioIndexById.get(radioId);
      if (existingIndex === undefined) {
        radioIndexById.set(radioId, result.length);
        result.push(field);
        return;
      }
      // A group keeps one entry, but it must be the checked option so the
      // default value matches the selection whatever the option order.
      if (field.radio?.checked) result[existingIndex] = field;
      return;
    }
    result.push(field);
  };

  fields.forEach((field) => {
    if (getFieldType(field) === 'grid') {
      getGridInnerFields(field).forEach(pushField);
      return;
    }
    pushField(field);
  });
  return result;
}

/**
 * Unique react-scroll name for a fieldset within a form panel stack layer.
 * @param {string | undefined} containerId - Scroll container id (unique per panel layer)
 * @param {string | number} fieldsetId - Fieldset id from form layout
 * @returns {string} Namespaced scroll target name
 */
export function getFieldsetScrollName(containerId, fieldsetId) {
  return `${containerId ?? 'form'}-fieldset-${fieldsetId}`;
}

/**
 * Whether the field participates in live search (text fields with liveSearchEnabled).
 * @param {FormItem} field - Form item
 * @returns {boolean}
 */
export function isLiveSearchEnabledField(field) {
  return Boolean(field.text?.liveSearchEnabled);
}

/**
 * Autocomplete URL for text fields (existing column values).
 * @param {FormItem} field - Form item
 * @returns {string | null}
 */
export function getFieldSuggestionsUrl(field) {
  return field.text?.suggestionsUrl ?? null;
}

/**
 * Whether the field offers inline suggestions (suggestionsUrl is non-null).
 * @param {FormItem} field - Form item
 * @returns {boolean}
 */
export function hasFieldSuggestions(field) {
  return Boolean(getFieldSuggestionsUrl(field));
}

/**
 * Get field label from FormItem
 * @param {FormItem} field - Form item
 * @returns {string} Field label
 */
export function getFieldLib(field) {
  const fieldData = getFieldData(field);
  if (fieldData && 'lib' in fieldData) return fieldData.lib || '';
  return '';
}

/**
 * Whether the field label should be bold.
 * @param {FormItem} field - Form item
 * @returns {boolean}
 */
export function getFieldLabelBold(field) {
  const fieldData = getFieldData(field);
  return Boolean(fieldData && 'bold' in fieldData && fieldData.bold);
}

/**
 * Whether the field label should be italic.
 * @param {FormItem} field - Form item
 * @returns {boolean}
 */
export function getFieldLabelItalic(field) {
  const fieldData = getFieldData(field);
  return Boolean(fieldData && 'italic' in fieldData && fieldData.italic);
}

/**
 * Label text color from FormItem (null when unset or empty).
 * @param {FormItem} field - Form item
 * @returns {string | null}
 */
export function getFieldLabelColor(field) {
  const fieldData = getFieldData(field);
  const color = fieldData && 'color' in fieldData ? fieldData.color : null;
  return typeof color === 'string' && color ? color : null;
}

/**
 * Get field value from FormItem (handles arrays for selectMultiple)
 * @param {FormItem} field - Form item
 * @returns {string | string[] | null} Field value (string or array for selectMultiple)
 */
export function getFieldValue(field) {
  if (field.selectMultiple && 'values' in field.selectMultiple) {
    return Array.isArray(field.selectMultiple.values) ? field.selectMultiple.values : [];
  }
  if (field.selectMultipleClass && 'values' in field.selectMultipleClass) {
    return Array.isArray(field.selectMultipleClass.values) ? field.selectMultipleClass.values : [];
  }
  if (field.mergeTable && 'value' in field.mergeTable) {
    return Array.isArray(field.mergeTable.value) ? field.mergeTable.value : [];
  }
  if (field.checkboxList && 'values' in field.checkboxList) {
    return Array.isArray(field.checkboxList.values) ? field.checkboxList.values : [];
  }
  if (field.radio && 'value' in field.radio) {
    return field.radio.checked ? (field.radio.value ?? null) : null;
  }
  const fieldData = getFieldData(field);
  if (fieldData && 'value' in fieldData) {
    return typeof fieldData.value === 'string' ? fieldData.value : null;
  }
  return null;
}
/**
 * Get field default value from FormItem (handles arrays for selectMultiple)
 * @param {FormItem} field - Form item
 * @returns {string | string[] | null} Field default (string or array for selectMultiple)
 */
export function getFieldDefault(field) {
  if (field.selectMultiple && 'default' in field.selectMultiple) {
    return Array.isArray(field.selectMultiple.default) ? field.selectMultiple.default : [];
  }
  if (field.selectMultipleClass && 'default' in field.selectMultipleClass) {
    return Array.isArray(field.selectMultipleClass.default) ? field.selectMultipleClass.default : [];
  }
  if (field.mergeTable && 'default' in field.mergeTable) {
    return Array.isArray(field.mergeTable.default) ? field.mergeTable.default : [];
  }
  if (field.checkboxList && 'default' in field.checkboxList) {
    return Array.isArray(field.checkboxList.default) ? field.checkboxList.default : [];
  }
  const fieldData = getFieldData(field);
  if (fieldData && 'default' in fieldData) {
    return typeof fieldData.default === 'string' ? fieldData.default : null;
  }
  return null;
}

/**
 * Get field help text from FormItem
 * @param {FormItem} field - Form item
 * @returns {string | null} Help text
 */
export function getFieldHelp(field) {
  const fieldData = getFieldData(field);
  if (fieldData && 'help' in fieldData) {
    return fieldData.help || null;
  }
  return null;
}

/**
 * Get field validation rules from FormItem
 * @param {FormItem} field - Form item
 * @returns {FormItemValidation} Validation rules
 */
export function getFieldValidation(field) {
  const fieldData = getFieldData(field);
  if (fieldData && 'validation' in fieldData) {
    return fieldData.validation || null;
  }
  return null;
}

/**
 * Get field options from FormItem (for select/selectMultiple)
 * @param {FormItem} field - Form item
 * @returns {SelectOption[]} Options array
 */
export function getFieldOptions(field) {
  if (field.select && 'options' in field.select) {
    return Array.isArray(field.select.options) ? field.select.options : [];
  }
  if (field.selectMultiple && 'options' in field.selectMultiple) {
    return Array.isArray(field.selectMultiple.options) ? field.selectMultiple.options : [];
  }
  return [];
}

/**
 * Check if field is multiple select
 * @param {FormItem} field - Form item
 * @returns {boolean} True if selectMultiple or selectMultipleClass
 */
export function isFieldMultiple(field) {
  return !!(field.selectMultiple || field.selectMultipleClass);
}

/** @typedef {'ANY' | 'ALL'} HideConditionsStrategy */

/** How multiple hideConditions combine: ANY = OR (default), ALL = AND */
export const HIDE_CONDITIONS_STRATEGY = /** @type {const} */ ({
  ANY: 'ANY',
  ALL: 'ALL',
});

/** @type {HideConditionsStrategy} */
export const DEFAULT_HIDE_CONDITIONS_STRATEGY = HIDE_CONDITIONS_STRATEGY.ANY;

/**
 * Normalize the current form value for a referenced field to string[] for hide matching.
 * Multi-select: intersection match ("at least one selected value is in condition.values").
 * @param {Record<string, unknown>} formValues
 * @param {string} refId
 * @param {Record<string, FormItem>} fieldsById
 * @returns {string[]}
 */
export function getReferencedFieldValuesAsStrings(formValues, refId, fieldsById) {
  const refField = fieldsById[refId];
  if (!refField) return [];
  // formValues keys are RHF names (encoded when the API id has [ ] .)
  const raw = formValues[encodeFieldName(refId)];

  if (isFieldMultiple(refField)) {
    if (!Array.isArray(raw) || raw.length === 0) return [];
    return raw.map((item) => {
      if (typeof item === 'object' && item !== null && 'value' in item) {
        return String(/** @type {{ value: unknown }} */ (item).value);
      }
      return String(item);
    });
  }

  const fieldType = getFieldType(refField);
  if (fieldType === 'checkboxList') {
    if (!Array.isArray(raw) || raw.length === 0) return [];
    return raw.map((item) => String(item));
  }

  if (fieldType === 'checkbox') {
    const isChecked = raw === true || raw === 'true' || raw === 'on';
    if (!isChecked) return [];
    return [String(refField.checkbox?.value ?? 'on')];
  }

  if (fieldType === 'mergeTable') {
    if (!Array.isArray(raw)) return [];
    return raw.map((item) => (
      typeof item === 'object' && item !== null ? JSON.stringify(item) : String(item)
    ));
  }

  if (raw === null || raw === undefined || raw === '') return [];
  return [String(raw)];
}

/**
 * @param {FormItemCondition} condition
 * @param {Record<string, unknown>} formValues
 * @param {Record<string, FormItem>} fieldsById
 * @returns {boolean}
 */
export function evaluateCondition(condition, formValues, fieldsById) {
  const selected = getReferencedFieldValuesAsStrings(formValues, condition.id, fieldsById);
  const condVals = Array.isArray(condition.values)
    ? condition.values.map((v) => String(v))
    : [];
  switch (condition.operator ?? 'in') {
    case 'isEmpty':
      return selected.length === 0 || selected.every((s) => s === '');
    case 'notIn':
      return !selected.some((s) => condVals.includes(s));
    default:
      return selected.some((s) => condVals.includes(s));
  }
}

/**
 * @param {FormItemHideCondition} condition
 * @param {Record<string, unknown>} formValues
 * @param {Record<string, FormItem>} fieldsById
 * @returns {boolean}
 */
export function evaluateHideCondition(condition, formValues, fieldsById) {
  return evaluateCondition(condition, formValues, fieldsById);
}

/**
 * True when at least one condition matches (OR semantics).
 * @param {FormItemCondition[]} conditions
 * @param {Record<string, unknown>} formValues
 * @param {Record<string, FormItem>} fieldsById
 * @returns {boolean}
 */
export function matchesAnyCondition(conditions, formValues, fieldsById) {
  if (!conditions?.length) return true;
  return conditions.some((c) => evaluateCondition(c, formValues, fieldsById));
}

/**
 * @param {FormItemCondition[]} hideConditions
 * @param {Record<string, unknown>} formValues
 * @param {Record<string, FormItem>} fieldsById
 * @param {HideConditionsStrategy} [strategy]
 * @returns {boolean}
 */
export function isRowVisibleByConditions(
  hideConditions,
  formValues,
  fieldsById,
  strategy = DEFAULT_HIDE_CONDITIONS_STRATEGY,
) {
  if (!formValues || !fieldsById) return true;
  const conds = hideConditions ?? [];
  if (conds.length === 0) return true;
  const matches = conds.map((c) => evaluateCondition(c, formValues, fieldsById));
  const isHidden = strategy === HIDE_CONDITIONS_STRATEGY.ALL
    ? matches.every(Boolean)
    : matches.some(Boolean);
  return !isHidden;
}

/**
 * @param {FormTableRow} row
 * @param {Record<string, unknown>} formValues
 * @param {Record<string, FormItem>} fieldsById
 * @param {HideConditionsStrategy} [strategy]
 * @returns {boolean}
 */
export function isRowVisible(
  row,
  formValues,
  fieldsById,
  strategy = DEFAULT_HIDE_CONDITIONS_STRATEGY,
) {
  return isRowVisibleByConditions(row.hideConditions, formValues, fieldsById, strategy);
}

/**
 * Check if a field should be visible (not hidden by hideConditions).
 * Without formValues/fieldsById, all fields are visible (backward compatible).
 * @param {FormItem} field
 * @param {Record<string, unknown>} [formValues]
 * @param {Record<string, FormItem>} [fieldsById]
 * @param {HideConditionsStrategy} [strategy]
 * @returns {boolean}
 */
export function isFieldVisible(
  field,
  formValues,
  fieldsById,
  strategy = DEFAULT_HIDE_CONDITIONS_STRATEGY,
) {
  if (!formValues || !fieldsById) return true;

  const data = getFieldData(field);
  const conds = data?.hideConditions ?? [];
  if (conds.length === 0) return true;

  const matches = conds.map((c) => evaluateCondition(c, formValues, fieldsById));
  const isHidden = strategy === HIDE_CONDITIONS_STRATEGY.ALL
    ? matches.every(Boolean)
    : matches.some(Boolean);
  return !isHidden;
}

/**
 * True when field is read-only (posted but not user-editable).
 * readOnlyConditions take precedence over the readOnly boolean when non-empty.
 * @param {FormItem} field
 * @param {Record<string, unknown>} [formValues]
 * @param {Record<string, FormItem>} [fieldsById]
 * @returns {boolean}
 */
export function isFieldReadOnly(field, formValues, fieldsById) {
  if (!formValues || !fieldsById) return false;
  const data = /** @type {{ readOnly?: boolean, readOnlyConditions?: FormItemCondition[] } | null} */ (
    getFieldData(field)
  );
  const conds = data?.readOnlyConditions ?? [];
  if (conds.length > 0) {
    return conds.some((c) => evaluateCondition(c, formValues, fieldsById));
  }
  return data?.readOnly === true;
}

/**
 * True when at least one disabled condition matches (OR semantics).
 * @param {FormItem} field
 * @param {Record<string, unknown>} [formValues]
 * @param {Record<string, FormItem>} [fieldsById]
 * @returns {boolean}
 */
export function isFieldDisabled(field, formValues, fieldsById) {
  if (!formValues || !fieldsById) return false;
  const data = /** @type {{ disabledConditions?: FormItemCondition[] } | null} */ (
    getFieldData(field)
  );
  const conds = data?.disabledConditions ?? [];
  if (conds.length === 0) return false;
  return conds.some((c) => evaluateCondition(c, formValues, fieldsById));
}

/**
 * Get field type configuration
 * @param {string} type - Field type
 * @returns {FieldTypeConfig} Field configuration
 */
export function getFieldTypeConfig(type) {
  return FIELD_TYPE_MAP[type] || FIELD_TYPE_MAP.text;
}

/**
 * Build Zod schema from form configuration
 * @param {ApiFormResponse} formConfig - Form configuration object
 * @returns {z.ZodSchema} Zod schema for form validation
 */
export function buildZodSchema(formConfig) {
  /** @type {Record<string, z.ZodSchema>} */
  const schemaShape = {};

  // Iterate through all fields in the structure
  formConfig.tabs.forEach((tab) => {
    tab.fieldsets.forEach((fieldset) => {
      getSubmittableFields(fieldset.fields).forEach((field) => {
        const fieldId = getFieldId(field);
        if (!fieldId) return;
        const rhfName = encodeFieldName(fieldId);

        const fieldType = getFieldType(field);
        if (fieldType === 'entity') {
          const fieldValidation = getFieldValidation(field);
          const entitySchema = z.object({
            values: z.array(z.string()),
            displayedValues: z.array(z.string()),
          });
          schemaShape[rhfName] = fieldValidation?.required
            ? entitySchema.refine(
              (data) => data.values.some((v) => v.trim() !== ''),
              { message: 'form.required' },
            )
            : entitySchema;
          return;
        }

        const fieldTypeConfig = getFieldTypeConfig(fieldType);
        const fieldValidation = getFieldValidation(field);
        const isMultiple = isFieldMultiple(field);

        if (fieldType === 'checkboxList') {
          const schema = z.array(z.string());
          schemaShape[rhfName] = fieldValidation?.required
            ? schema.min(1, 'form.required')
            : schema.default([]);
          return;
        }

        // Handle multiple select fields (selectMultiple, selectMultipleClass)
        // react-select returns array of objects {value: string, label: string}
        if (isMultiple) {
          /** @type {import('zod').ZodTypeAny} */
          const selectOptionSchema = z.object({
            value: z.string(),
            label: z.string(),
          });
          const schema = z.array(selectOptionSchema);
          const defaultSchema = schema.default([]);
          const requiredSchema = schema.min(1, 'form.required');
          schemaShape[rhfName] = fieldValidation?.required ? requiredSchema : defaultSchema;
        } else {
          const fieldForValidation = {
            id: fieldId,
            type: fieldType,
            validation: fieldValidation,
            default: getFieldDefault(field),
            ...getFieldData(field),
          };
          schemaShape[rhfName] = fieldTypeConfig.zodSchema(fieldForValidation);
        }
      });
    });
  });

  return z.object(schemaShape);
}

/**
 * Build default values from form configuration
 * @param {ApiFormResponse} formConfig - Form configuration object
 * @returns {Record<string, any>} Default values object for React Hook Form
 */
export function buildDefaultValues(formConfig) {
  /** @type {Record<string, any>} */
  const defaultValues = {};

  // Iterate through all fields in the OpenAPI structure
  formConfig.tabs.forEach((tab) => {
    tab.fieldsets.forEach((fieldset) => {
      getSubmittableFields(fieldset.fields).forEach((field) => {
        const fieldId = getFieldId(field);
        if (!fieldId) return;
        const rhfName = encodeFieldName(fieldId);

        const fieldType = getFieldType(field);
        if (fieldType === 'entity') {
          const entityField = field.entity;
          if (entityField) {
            defaultValues[rhfName] = {
              values: Array.isArray(entityField.values) ? [...entityField.values] : [],
              displayedValues: Array.isArray(entityField.displayedValues)
                ? [...entityField.displayedValues]
                : [],
            };
          }
          return;
        }

        if (fieldType === 'checkboxList') {
          const fieldValue = getFieldValue(field);
          const fieldDefault = getFieldDefault(field);
          if (Array.isArray(fieldValue) && fieldValue.length > 0) {
            defaultValues[rhfName] = [...fieldValue];
          } else if (Array.isArray(fieldDefault) && fieldDefault.length > 0) {
            defaultValues[rhfName] = [...fieldDefault];
          } else {
            defaultValues[rhfName] = [];
          }
          return;
        }

        if (fieldType === 'checkbox') {
          defaultValues[rhfName] = Boolean(field.checkbox?.checked);
          return;
        }

        const fieldTypeConfig = getFieldTypeConfig(fieldType);
        const fieldValue = getFieldValue(field);
        const fieldDefault = getFieldDefault(field);
        const fieldValidation = getFieldValidation(field);

        // Priority: field.value > field.default > type default
        let defaultValue;

        // Handle arrays for selectMultiple
        // Transform array of strings to array of objects for react-select format
        if (isFieldMultiple(field)) {
          const fieldOptions = getFieldOptions(field);
          // Helper to transform string array to object array
          const transformToSelectFormat = (/** @type {string[]} */ stringArray) => {
            if (!Array.isArray(stringArray) || stringArray.length === 0) return [];
            return stringArray.map((val) => {
              // Find matching option to get label
              const flatOptions = fieldOptions.flatMap((opt) => [opt]);
              const matchingOption = flatOptions.find((opt) => opt.id === val);
              return {
                value: val,
                label: matchingOption?.lib || val,
              };
            });
          };

          if (Array.isArray(fieldValue) && fieldValue.length > 0) {
            defaultValue = transformToSelectFormat(fieldValue);
          } else if (Array.isArray(fieldDefault) && fieldDefault.length > 0) {
            defaultValue = transformToSelectFormat(fieldDefault);
          } else {
            defaultValue = [];
          }
        } else if (fieldValue !== null && fieldValue !== undefined && fieldValue !== '') {
          // Handle string values
          defaultValue = fieldTypeConfig.parseValue(fieldValue);
        } else if (fieldDefault !== null && fieldDefault !== undefined && fieldDefault !== '') {
          defaultValue = fieldTypeConfig.parseValue(fieldDefault);
        } else {
          // Use type-specific default
          switch (fieldType) {
            case 'text':
            case 'textarea':
            case 'email':
            case 'datetime':
            case 'time':
              defaultValue = '';
              break;
            case 'int':
            case 'float':
              defaultValue = fieldValidation?.required ? 0 : undefined;
              break;
            case 'color':
              defaultValue = '#FFFFFF';
              break;
            case 'enum':
            case 'radio':
            case 'radioList':
              defaultValue = '';
              break;
            case 'boolean':
              // Boolean fields always have a value ('oui' or 'non')
              // Use 'oui' as default if no value/default provided
              defaultValue = 'oui';
              break;
            case 'file':
              defaultValue = null;
              break;
            case 'mergeTable':
              defaultValue = [];
              break;
            default:
              defaultValue = fieldValidation?.required ? '' : undefined;
          }
        }

        defaultValues[rhfName] = defaultValue;
      });
    });
  });

  return defaultValues;
}

/**
 * Process form layout from configuration into structured tabs and fieldsets
 * @param {ApiFormResponse} formConfig - Complete form configuration object
 * @returns {FormLayout} Processed layout with organized tabs, fieldsets, and indexed fields
 */
export function buildFormLayout(formConfig) {
  /** @type {Record<string, FormItem>} */
  const fieldsById = {};
  /** @type {Record<string, string>} */
  const gridParentIdByInnerFieldId = {};
  /** @type {Record<string, GridRowContext>} */
  const rowByInnerFieldId = {};

  // Return tabs directly (structure already correct in OpenAPI format)
  // Build fieldsById index by iterating through all fields
  const tabs = formConfig.tabs.map((tab) => ({
    id: tab.id,
    legend: tab.legend || '',
    subLegend: tab.subLegend || null,
    displayAnchors: tab.displayAnchors,
    fieldsets: tab.fieldsets.map((fieldset) => {
      // Build index for all fields in this fieldset (including grid inner fields)
      fieldset.fields.forEach((field) => {
        const fieldId = getFieldId(field);
        if (fieldId) {
          fieldsById[fieldId] = field;
        }
        if (getFieldType(field) === 'grid') {
          forEachGridInnerFieldInRows(field, ({ formItem, row }) => {
            const innerId = getFieldId(formItem);
            if (innerId) {
              fieldsById[innerId] = formItem;
              if (fieldId) {
                gridParentIdByInnerFieldId[innerId] = fieldId;
                rowByInnerFieldId[innerId] = {
                  rowId: row.rowId ?? null,
                  hideConditions: row.hideConditions ?? [],
                  parentGridId: fieldId,
                };
              }
            }
          });
        }
      });

      return {
        id: fieldset.id,
        legend: fieldset.legend || '',
        subLegend: fieldset.subLegend || null,
        column: fieldset.column || 0,
        fields: fieldset.fields, // Keep FormItem structure as-is
      };
    }),
  }));

  return {
    tabs,
    fieldsById,
    gridParentIdByInnerFieldId,
    rowByInnerFieldId,
  };
}

/**
 * Field IDs (RHF names) that pass visibility given current form values.
 * Keys match react-hook-form (encoded when the API id contains [ ] .).
 * @param {ApiFormResponse} formConfig
 * @param {Record<string, unknown>} formValues
 * @param {HideConditionsStrategy} [strategy]
 * @returns {Set<string>}
 */
export function getVisibleFieldIds(formConfig, formValues, strategy = DEFAULT_HIDE_CONDITIONS_STRATEGY) {
  const {
    fieldsById,
    gridParentIdByInnerFieldId,
    rowByInnerFieldId,
  } = buildFormLayout(formConfig);
  /** @type {Set<string>} */
  const visible = new Set();
  Object.entries(fieldsById).forEach(([id, field]) => {
    if (!isFieldVisible(field, formValues, fieldsById, strategy)) return;

    const parentGridId = gridParentIdByInnerFieldId[id];
    if (parentGridId) {
      const parentGrid = fieldsById[parentGridId];
      if (!parentGrid || !isFieldVisible(parentGrid, formValues, fieldsById, strategy)) {
        return;
      }
      const rowContext = rowByInnerFieldId[id];
      if (rowContext && !isRowVisibleByConditions(
        rowContext.hideConditions,
        formValues,
        fieldsById,
        strategy,
      )) {
        return;
      }
    }

    visible.add(encodeFieldName(id));
  });
  return visible;
}

/**
 * Field IDs (RHF names) that are disabled given current form values.
 * Disabled fields are excluded from POST (unlike readOnly).
 * @param {ApiFormResponse} formConfig
 * @param {Record<string, unknown>} formValues
 * @returns {Set<string>}
 */
export function getDisabledFieldIds(formConfig, formValues) {
  const { fieldsById } = buildFormLayout(formConfig);
  /** @type {Set<string>} */
  const disabled = new Set();
  Object.entries(fieldsById).forEach(([id, field]) => {
    if (!isFieldDisabled(field, formValues, fieldsById)) return;
    disabled.add(encodeFieldName(id));
  });
  return disabled;
}

/**
 * Sanitize and validate form data before submission
 * @param {Record<string, any>} formData - Raw form data from React Hook Form
 * @param {ApiFormResponse} formConfig - Original form configuration object
 * @returns {Record<string, any>} Sanitized form data ready for API submission
 */
export function sanitizeFormData(formData, formConfig) {
  /** @type {Record<string, any>} */
  const sanitized = {};
  const raw = formData || {};
  const visibleIds = getVisibleFieldIds(
    formConfig,
    /** @type {Record<string, unknown>} */ (raw),
  );
  const disabledIds = getDisabledFieldIds(
    formConfig,
    /** @type {Record<string, unknown>} */ (raw),
  );

  // Iterate through all fields in the OpenAPI structure
  formConfig.tabs.forEach((tab) => {
    tab.fieldsets.forEach((fieldset) => {
      getSubmittableFields(fieldset.fields).forEach((field) => {
        const fieldId = getFieldId(field);
        if (!fieldId) return;
        const rhfName = encodeFieldName(fieldId);

        if (!visibleIds.has(rhfName)) return;
        if (disabledIds.has(rhfName)) return;

        const fieldType = getFieldType(field);
        if (fieldType === 'entity') {
          const fieldTypeConfig = getFieldTypeConfig('entity');
          const parsed = fieldTypeConfig.parseValue(raw[rhfName]);
          sanitized[fieldId] = parsed;
          return;
        }

        if (fieldType === 'checkboxList') {
          const rawValue = raw[rhfName];
          if (Array.isArray(rawValue)) {
            sanitized[fieldId] = rawValue
              .filter((item) => typeof item === 'string' || typeof item === 'number')
              .map((item) => String(item));
          } else {
            sanitized[fieldId] = [];
          }
          return;
        }

        if (fieldType === 'checkbox') {
          sanitized[fieldId] = Boolean(raw[rhfName]);
          return;
        }

        const fieldTypeConfig = getFieldTypeConfig(fieldType);
        const fieldValidation = getFieldValidation(field);
        const rawValue = raw[rhfName];

        // Handle arrays for selectMultiple
        // Extract values from react-select format [{value, label}, ...] to [value, ...]
        if (isFieldMultiple(field)) {
          if (Array.isArray(rawValue)) {
            // Extract value property from each object
            sanitized[fieldId] = rawValue.map((item) => {
              if (typeof item === 'object' && item !== null && 'value' in item) {
                return item.value;
              }
              // Fallback if already a string (shouldn't happen but safety)
              return typeof item === 'string' ? item : '';
            });
          } else {
            sanitized[fieldId] = [];
          }
        } else {
          const parsedValue = fieldTypeConfig.parseValue(rawValue);

          // Handle null/undefined based on validation rules
          if (parsedValue === undefined || parsedValue === '') {
            if (fieldValidation?.required) {
              // For required fields, provide type-appropriate default
              switch (fieldType) {
                case 'int':
                case 'float':
                  sanitized[fieldId] = 0;
                  break;
                case 'boolean':
                  // For required boolean, default to 'oui' if no value provided
                  sanitized[fieldId] = 'oui';
                  break;
                default:
                  sanitized[fieldId] = '';
              }
            } else {
              sanitized[fieldId] = null;
            }
          } else if (fieldType === 'boolean') {
            // Validate boolean values are 'oui' or 'non', default to 'oui' if invalid
            sanitized[fieldId] = (parsedValue === 'oui' || parsedValue === 'non') ? parsedValue : 'oui';
          } else {
            sanitized[fieldId] = parsedValue;
          }
        }
      });
    });
  });

  return sanitized;
}

/**
 * Transform sanitized form data to API submission format
 * @param {Record<string, any>} sanitizedData - Sanitized form data (flat object with fieldId as keys)
 * @param {ApiFormResponse} formConfig - Original form configuration object
 * @param {ApiFormSubmissionField[]} [submissionParams] - Resolved submission params (override or formConfig.params)
 * @returns {ApiFormPostRequestBody} Form data in API format
 */
export function transformFormDataForSubmission(sanitizedData, formConfig, submissionParams) {
  /** @type {ApiFormSubmissionField[]} */
  const fields = [];

  formConfig.tabs.forEach((tab) => {
    tab.fieldsets.forEach((fieldset) => {
      getSubmittableFields(fieldset.fields).forEach((field) => {
        const fieldId = getFieldId(field);
        if (!fieldId) return;

        if (!Object.prototype.hasOwnProperty.call(sanitizedData, fieldId)) return;

        const fieldType = getFieldType(field);

        if (fieldType === 'entity') {
          const entityValue = sanitizedData[fieldId];
          const ids = typeof entityValue === 'object' && entityValue !== null
            && Array.isArray(entityValue.values)
            ? entityValue.values.filter((id) => typeof id === 'string' && id.trim() !== '')
            : [];
          fields.push({
            id: fieldId,
            values: ids.length ? ids : [''],
            singleValue: !field.entity?.multiple,
          });
          return;
        }

        const isMultiple = isFieldMultiple(field);
        const fieldValue = sanitizedData[fieldId];

        if (fieldType === 'mergeTable') {
          fields.push({
            id: fieldId,
            values: Array.isArray(fieldValue) ? fieldValue.map((s) => JSON.stringify(s)) : [],
            singleValue: false,
          });
          return;
        }

        if (fieldType === 'checkboxList') {
          fields.push({
            id: fieldId,
            values: Array.isArray(fieldValue) ? fieldValue.map((v) => String(v)) : [],
            singleValue: false,
          });
          return;
        }

        if (fieldType === 'checkbox') {
          if (!fieldValue) return;
          const checkboxValue = field.checkbox?.value;
          fields.push({
            id: fieldId,
            values: [checkboxValue ?? 'on'],
            singleValue: true,
          });
          return;
        }

        const singleValue = !isMultiple;

        let values = [];
        if (isMultiple) {
          values = Array.isArray(fieldValue) ? fieldValue.map((v) => String(v)) : [];
        } else {
          // Handle null/undefined by using empty array or empty string
          values = (fieldValue === null || fieldValue === undefined) ? [''] : [String(fieldValue)];
        }

        fields.push({
          id: fieldId,
          values,
          singleValue,
        });
      });
    });
  });

  const params = submissionParams ?? formConfig.params ?? [];

  return { fields, params };
}

/**
 * Merge a fieldsToUpdate item into the current entity field value.
 * @param {{ values?: string[], displayedValues?: string[] } | null | undefined} currentValue
 * @param {ApiFormFieldsToUpdateItem} item
 * @param {boolean} isMultiple
 * @returns {{ values: string[], displayedValues: string[] }}
 */
export function mergeEntityFieldValue(currentValue, item, isMultiple) {
  const values = Array.isArray(currentValue?.values) ? [...currentValue.values] : [];
  const displayedValues = Array.isArray(currentValue?.displayedValues)
    ? [...currentValue.displayedValues]
    : [];

  const nextValue = typeof item.value === 'string' ? item.value : '';
  const nextDisplayed = typeof item.displayedValue === 'string' ? item.displayedValue : '';

  if (!nextValue.trim()) {
    return { values, displayedValues };
  }

  const originalValue = item.originalValue ?? null;
  const index = originalValue ? values.indexOf(originalValue) : -1;

  if (index >= 0) {
    values[index] = nextValue;
    displayedValues[index] = nextDisplayed;
    return { values, displayedValues };
  }

  if (isMultiple) {
    if (values.includes(nextValue)) {
      return { values, displayedValues };
    }
    values.push(nextValue);
    displayedValues.push(nextDisplayed);
    return { values, displayedValues };
  }

  return {
    values: [nextValue],
    displayedValues: [nextDisplayed],
  };
}

/**
 * Apply fieldsToUpdate from a child form POST response to the parent form.
 * @param {ApiFormFieldsToUpdateItem[]} fieldsToUpdate
 * @param {import('react-hook-form').UseFormSetValue<Record<string, unknown>>} setValue
 * @param {FormLayout} layout
 * @param {import('react-hook-form').UseFormGetValues<Record<string, unknown>>} [getValues]
 */
export function applyFieldsToUpdate(fieldsToUpdate, setValue, layout, getValues) {
  fieldsToUpdate.forEach((item) => {
    const field = layout.fieldsById[item.id];
    if (!field) return;
    const rhfName = encodeFieldName(item.id);

    const fieldType = getFieldType(field);

    if (fieldType === 'entity') {
      const currentValue = getValues
        ? /** @type {{ values?: string[], displayedValues?: string[] } | undefined} */ (
          getValues(rhfName)
        )
        : undefined;
      setValue(
        rhfName,
        mergeEntityFieldValue(currentValue, item, Boolean(field.entity?.multiple)),
        { shouldDirty: true, shouldValidate: true },
      );
      return;
    }

    if (isFieldMultiple(field)) {
      const fieldOptions = getFieldOptions(field);
      const values = (item.value ? [item.value] : []).map((val) => {
        const matchingOption = fieldOptions.find((opt) => opt.id === val);
        return {
          value: val,
          label: matchingOption?.lib ?? val,
        };
      });
      setValue(rhfName, values, { shouldDirty: true, shouldValidate: true });
      return;
    }

    setValue(rhfName, item.value ?? '', { shouldDirty: true, shouldValidate: true });
  });
}

/**
 * Panel title from loaded form configuration (first tab legend).
 * @param {ApiFormResponse | undefined} formConfig
 * @returns {string}
 */
export function getFormPanelTitle(formConfig) {
  return formConfig?.tabs?.[0]?.legend ?? '';
}
